import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../sections/Navbar';
import MenuHeroSlider from '../sections/MenuHeroSlider';
import Footer from '../sections/Footer';
import AiSuggestModal from '../components/AiSuggestModal';
import MenuItemCard from '../components/MenuItemCard';
import { Button } from 'antd';
import { Search, ChevronDown } from 'lucide-react'; // Import ChevronDown for collapsibles

// --- Import the static JSON data directly ---
import menuData from '../data/menuDrinks.json';

const slugify = (text) => text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');

// --- A Set to quickly identify which categories are considered "Food" ---
const FOOD_CATEGORIES = new Set(['Ftourat', 'Poke Bowls', 'Cakes', 'Breakfast & Pastries']);

// --- MAIN SCREEN COMPONENT ---
const MenuScreen = () => {
  const allMenuDrinks = useMemo(() => menuData.menuDrinks || [], []);
  
  // --- State to track the active category (can now be a parent or child) ---
  const [activeCategory, setActiveCategory] = useState('all'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [isAiModalVisible, setIsAiModalVisible] = useState(false);
  const mainContentRef = useRef(null);
  
  // --- NEW: State for collapsible sections ---
  const [expanded, setExpanded] = useState({ Drinks: true, Food: true });

  // --- REFACTORED: Data grouping logic to create parent categories ---
  const { menuStructure, productsByCategory } = useMemo(() => {
    const structure = { Drinks: [], Food: [] };
    const productsGrouped = {};

    const filteredItems = allMenuDrinks.filter(p => !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase()));

    filteredItems.forEach(product => {
      const category = product.category;
      if (category && category !== 'Highlighted drinks') {
        if (!productsGrouped[category]) {
          productsGrouped[category] = [];
          const parent = FOOD_CATEGORIES.has(category) ? 'Food' : 'Drinks';
          if (!structure[parent].includes(category)) {
            structure[parent].push(category);
          }
        }
        productsGrouped[category].push(product);
      }
    });

    // Sort the sub-categories alphabetically
    structure.Drinks.sort();
    structure.Food.sort();

    return { menuStructure: structure, productsByCategory: productsGrouped };
  }, [allMenuDrinks, searchTerm]);
  
  const handleCategorySelect = (category) => {
    setActiveCategory(category);
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Determine which categories to show in the main content area
  let displayedCategories = [];
  if (activeCategory === 'all') {
    displayedCategories = [...menuStructure.Drinks, ...menuStructure.Food];
  } else if (activeCategory === 'Drinks' || activeCategory === 'Food') {
    displayedCategories = menuStructure[activeCategory];
  } else {
    displayedCategories = [activeCategory];
  }
  
  const toggleExpand = (parentCategory) => {
    setExpanded(prev => ({ ...prev, [parentCategory]: !prev[parentCategory] }));
  };

  return (
    <>
      <Navbar />
      <MenuHeroSlider />
      <div className="menu-page-layout">
        {/* --- 1. The Sticky Sidebar (with updated logic) --- */}
        <aside className="menu-sidebar">
          {/* <ul>
            <li className={activeCategory === 'all' ? 'active' : ''}>
              <a onClick={() => handleCategorySelect('all')}>All Menu</a>
            </li>
          </ul> */}

          {Object.keys(menuStructure).map(parentCategory => (
            menuStructure[parentCategory].length > 0 && (
              <div key={parentCategory} className="sidebar-parent-category">
                <h3 onClick={() => toggleExpand(parentCategory)}>
                  {parentCategory}
                  <ChevronDown className={`chevron-icon ${expanded[parentCategory] ? 'expanded' : ''}`} size={18} />
                </h3>
                {expanded[parentCategory] && (
                  <ul>
                    {menuStructure[parentCategory].map(category => (
                      <li key={category} className={activeCategory === category ? 'active' : ''}>
                        <a onClick={() => handleCategorySelect(category)}>{category}</a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          ))}
        </aside>

        {/* --- 2. The Main Scrolling Content (with updated logic) --- */}
        <main className="menu-content" ref={mainContentRef}>
          <header className="menu-content-header">
            <h1>Our Offerings</h1>
            <div className="menu-controls">
              <Button className='ai-suggestion-button' onClick={() => setIsAiModalVisible(true)}>AI Drink Suggestion</Button>
              <div className="search-bar-container">
                <Search size={20} className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search our menu..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                />
              </div>
            </div>
          </header>

           {(displayedCategories.length === 0 || Object.keys(productsByCategory).length === 0) && searchTerm && (
            <p className="no-results-message">No items found for "{searchTerm}".</p>
          )}

          {/* --- NEW: AnimatePresence for smooth transitions --- */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {displayedCategories.map(category => (
                productsByCategory[category] && (
                  <section key={category} className="menu-category-section" id={slugify(category)}>
                    
                    <div className="menu-items-grid">
                      
                      {productsByCategory[category].map(product => (
                        // ===================================================================
                        // --- NAVIGATION FEATURE COMMENTED OUT ---
                        // ===================================================================
                        // Previously, we rendered <MenuItemCard product={product} />.
                        // That component contained a <Link>. To disable the navigation,
                        // we now render the card's JSX directly here and comment out
                        // the <Link> wrapper.

                        /* 
                        // To re-enable navigation, uncomment this Link wrapper and the closing tag below.
                        <Link to={`/menu/${product.id}`} className="menu-item-link" key={product.id}>
                        */

                          <div className="menu-item-card">
                            <div className="menu-item-image-wrapper">
                              <img src={product.mainImage} alt={product.name} />
                            </div>
                            <h3 className="menu-item-name">{product.name}</h3>
                          </div>

                        /*
                        </Link>
                        */
                        // ===================================================================
                      ))}
                    </div>
                  </section>
                )
              ))}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      
      <AiSuggestModal open={isAiModalVisible} onClose={() => setIsAiModalVisible(false)} menuDrinks={allMenuDrinks}/>
      <Footer />
    </>
  );
};

export default MenuScreen;